import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { BookResult } from '@/types/search';

interface UseBookSearchOptions {
  limit?: number;
  debounceMs?: number;
}

interface UseBookSearchReturn {
  results: BookResult[];
  isLoading: boolean;
  error: string | null;
  search: (query: string, type?: 'title' | 'author') => void;
  searchImmediate: (query: string, type?: 'title' | 'author') => Promise<void>;
  clear: () => void;
}

/**
 * Unified book search hook with built-in debounce, request cancellation,
 * and stability window to prevent result flashing.
 * 
 * Features:
 * - 350ms debounce (configurable)
 * - AbortController for request cancellation
 * - Stability window: only updates results if query hasn't changed
 * - Query ref tracking to detect stale responses
 */
export function useBookSearch(options: UseBookSearchOptions = {}): UseBookSearchReturn {
  const { limit = 20, debounceMs = 350 } = options;
  
  const [results, setResults] = useState<BookResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Request cancellation and staleness tracking
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentQueryRef = useRef<string>('');
  const currentTypeRef = useRef<'title' | 'author'>('title');
  const debounceRef = useRef<NodeJS.Timeout>();
  const stabilityTimeoutRef = useRef<NodeJS.Timeout>();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (stabilityTimeoutRef.current) clearTimeout(stabilityTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Immediate search (no debounce) - internal use
  const searchImmediate = useCallback(async (query: string, type: 'title' | 'author' = 'title') => {
    const trimmedQuery = query.trim();
    
    // Update refs before any async work
    currentQueryRef.current = trimmedQuery;
    currentTypeRef.current = type;
    
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setResults([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      // Edge function handles local DB + external sources internally
      const { data, error: fnError } = await supabase.functions.invoke('search-books', {
        body: { query: trimmedQuery, type, page: 1, limit },
      });

      // Check if request was aborted or query changed
      if (controller.signal.aborted) {
        return;
      }

      // Staleness check: only update if this is still the current query
      if (currentQueryRef.current !== trimmedQuery || currentTypeRef.current !== type) {
        console.log('[useBookSearch] Dropping stale response for:', trimmedQuery);
        return;
      }

      if (fnError) throw fnError;

      // Stability window: wait 100ms to ensure query is stable before updating
      if (stabilityTimeoutRef.current) {
        clearTimeout(stabilityTimeoutRef.current);
      }
      
      stabilityTimeoutRef.current = setTimeout(() => {
        // Final staleness check after stability window
        if (currentQueryRef.current === trimmedQuery && currentTypeRef.current === type) {
          setResults(data?.results || []);
        }
      }, 100);
      
    } catch (err) {
      // Don't update state if request was cancelled
      if (controller.signal.aborted) {
        return;
      }
      
      // Only set error if this is still the current query
      if (currentQueryRef.current === trimmedQuery) {
        console.error('[useBookSearch] Search error:', err);
        setError('Search failed. Please try again.');
        setResults([]);
      }
    } finally {
      // Only clear loading if this is still the current query
      if (currentQueryRef.current === trimmedQuery && !controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [limit]);

  // Debounced search - primary entry point
  const search = useCallback((query: string, type: 'title' | 'author' = 'title') => {
    // Clear existing debounce timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    const trimmedQuery = query.trim();
    
    // Clear results immediately if query is too short
    if (!trimmedQuery || trimmedQuery.length < 2) {
      currentQueryRef.current = '';
      setResults([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Show loading state immediately for better perceived performance
    setIsLoading(true);
    
    // Debounce the actual search
    debounceRef.current = setTimeout(() => {
      searchImmediate(query, type);
    }, debounceMs);
  }, [debounceMs, searchImmediate]);

  const clear = useCallback(() => {
    // Cancel any pending operations
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (stabilityTimeoutRef.current) clearTimeout(stabilityTimeoutRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    
    currentQueryRef.current = '';
    setResults([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    results,
    isLoading,
    error,
    search,
    searchImmediate,
    clear,
  };
}
